import { errorTracker, logError } from './global-handlers';
import { toast } from 'sonner';

/**
 * Comprehensive error tracking utilities for different scenarios
 */

// 1. Async Operation Wrapper
export async function trackAsync<T>(
  operation: () => Promise<T>,
  context: {
    operation: string;
    component?: string;
    metadata?: Record<string, any>;
  }
): Promise<T> {
  try {
    const startTime = Date.now();
    const result = await operation();
    
    // Track successful operations for performance monitoring
    errorTracker.addBreadcrumb({
      timestamp: new Date().toISOString(),
      type: 'custom',
      category: 'async',
      message: `${context.operation} completed`,
      level: 'info',
      data: {
        duration: Date.now() - startTime,
        ...context.metadata
      }
    });
    
    return result;
  } catch (error) {
    // Enhanced error tracking with context
    await logError(error as Error, {
      operation: context.operation,
      component: context.component,
      asyncError: true,
      ...context.metadata
    });
    
    throw error; // Re-throw to maintain error flow
  }
}

// 2. Form Submission Tracker
export async function trackFormSubmission<T>(
  submitFn: () => Promise<T>,
  options: {
    formName: string;
    showToast?: boolean;
    successMessage?: string;
    errorMessage?: string;
  }
): Promise<{ success: boolean; data?: T; error?: Error }> {
  try {
    errorTracker.addBreadcrumb({
      timestamp: new Date().toISOString(),
      type: 'user',
      category: 'form',
      message: `Submitting form: ${options.formName}`,
      level: 'info'
    });
    
    const data = await submitFn();
    
    if (options.showToast && options.successMessage) {
      toast.success(options.successMessage);
    }
    
    return { success: true, data };
  } catch (error) {
    const err = error as Error;
    
    await logError(err, {
      formName: options.formName,
      formError: true,
      userAction: 'form_submission'
    });
    
    if (options.showToast) {
      toast.error(options.errorMessage || `Failed to submit ${options.formName}`);
    }
    
    return { success: false, error: err };
  }
}

// 3. Network Request Wrapper with Retry
export async function trackNetworkRequest<T>(
  request: () => Promise<Response>,
  options: {
    url: string;
    method: string;
    retries?: number;
    retryDelay?: number;
    timeout?: number;
  }
): Promise<T> {
  const maxRetries = options.retries || 3;
  const retryDelay = options.retryDelay || 1000;
  const timeout = options.timeout || 30000;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Add timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      errorTracker.addBreadcrumb({
        timestamp: new Date().toISOString(),
        type: 'api',
        category: 'network',
        message: `${options.method} ${options.url} (attempt ${attempt})`,
        level: 'info'
      });
      
      const response = await Promise.race([
        request(),
        new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), timeout)
        )
      ]);
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        (error as any).status = response.status;
        (error as any).response = response;
        throw error;
      }
      
      const data = await response.json();
      return data as T;
      
    } catch (error) {
      const isLastAttempt = attempt === maxRetries;
      
      // Log the error with retry context
      await logError(error as Error, {
        url: options.url,
        method: options.method,
        attempt,
        maxRetries,
        isTimeout: error instanceof Error && error.message === 'Request timeout',
        isNetworkError: true
      });
      
      if (!isLastAttempt) {
        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
        continue;
      }
      
      throw error;
    }
  }
  
  throw new Error('Max retries exceeded');
}

// 4. Real-time Subscription Error Tracking
export function trackRealtimeSubscription(
  channel: string,
  handlers: {
    onConnect?: () => void;
    onDisconnect?: () => void;
    onError?: (error: Error) => void;
    onReconnect?: () => void;
  }
) {
  let reconnectAttempts = 0;
  let lastError: Error | null = null;
  
  return {
    handleError: async (error: Error) => {
      reconnectAttempts++;
      lastError = error;
      
      await logError(error, {
        channel,
        reconnectAttempts,
        realtimeError: true,
        lastSuccessfulConnection: new Date().toISOString()
      });
      
      if (handlers.onError) {
        handlers.onError(error);
      }
    },
    
    handleReconnect: () => {
      errorTracker.addBreadcrumb({
        timestamp: new Date().toISOString(),
        type: 'custom',
        category: 'realtime',
        message: `Reconnected to channel: ${channel}`,
        level: 'info',
        data: {
          reconnectAttempts,
          lastError: lastError?.message
        }
      });
      
      reconnectAttempts = 0;
      lastError = null;
      
      if (handlers.onReconnect) {
        handlers.onReconnect();
      }
    }
  };
}

// 5. Component Error Boundary Helper
export function createComponentErrorHandler(componentName: string) {
  return {
    logError: (error: Error, errorInfo?: any) => {
      logError(error, {
        component: componentName,
        errorBoundary: true,
        errorInfo
      });
    },
    
    trackAction: (action: string, data?: any) => {
      errorTracker.addBreadcrumb({
        timestamp: new Date().toISOString(),
        type: 'user',
        category: 'component',
        message: `${componentName}: ${action}`,
        level: 'info',
        data
      });
    },
    
    trackMount: () => {
      errorTracker.addBreadcrumb({
        timestamp: new Date().toISOString(),
        type: 'custom',
        category: 'lifecycle',
        message: `${componentName} mounted`,
        level: 'info'
      });
    },
    
    trackUnmount: () => {
      errorTracker.addBreadcrumb({
        timestamp: new Date().toISOString(),
        type: 'custom',
        category: 'lifecycle',
        message: `${componentName} unmounted`,
        level: 'info'
      });
    }
  };
}

// 6. File Operation Tracker
export async function trackFileOperation<T>(
  operation: () => Promise<T>,
  context: {
    fileName: string;
    operation: 'upload' | 'download' | 'delete' | 'read';
    fileSize?: number;
    fileType?: string;
  }
): Promise<T> {
  const startTime = Date.now();
  
  try {
    errorTracker.addBreadcrumb({
      timestamp: new Date().toISOString(),
      type: 'custom',
      category: 'file',
      message: `Starting ${context.operation}: ${context.fileName}`,
      level: 'info',
      data: context
    });
    
    const result = await operation();
    
    errorTracker.addBreadcrumb({
      timestamp: new Date().toISOString(),
      type: 'custom',
      category: 'file',
      message: `Completed ${context.operation}: ${context.fileName}`,
      level: 'info',
      data: {
        ...context,
        duration: Date.now() - startTime
      }
    });
    
    return result;
  } catch (error) {
    await logError(error as Error, {
      fileOperation: context.operation,
      fileName: context.fileName,
      fileSize: context.fileSize,
      fileType: context.fileType,
      duration: Date.now() - startTime
    });
    
    throw error;
  }
}

// 7. Background Task Tracker
export class BackgroundTaskTracker {
  private taskId: string;
  private startTime: number;
  private checkpoints: Array<{ name: string; timestamp: number }> = [];
  
  constructor(taskName: string, metadata?: Record<string, any>) {
    this.taskId = `${taskName}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.startTime = Date.now();
    
    errorTracker.addBreadcrumb({
      timestamp: new Date().toISOString(),
      type: 'custom',
      category: 'background-task',
      message: `Started task: ${taskName}`,
      level: 'info',
      data: {
        taskId: this.taskId,
        ...metadata
      }
    });
  }
  
  checkpoint(name: string, data?: any) {
    const timestamp = Date.now();
    this.checkpoints.push({ name, timestamp });
    
    errorTracker.addBreadcrumb({
      timestamp: new Date().toISOString(),
      type: 'custom',
      category: 'background-task',
      message: `Task checkpoint: ${name}`,
      level: 'info',
      data: {
        taskId: this.taskId,
        duration: timestamp - this.startTime,
        checkpoint: name,
        ...data
      }
    });
  }
  
  async complete(result?: any) {
    const duration = Date.now() - this.startTime;
    
    errorTracker.addBreadcrumb({
      timestamp: new Date().toISOString(),
      type: 'custom',
      category: 'background-task',
      message: `Completed task: ${this.taskId}`,
      level: 'info',
      data: {
        taskId: this.taskId,
        duration,
        checkpoints: this.checkpoints,
        result
      }
    });
  }
  
  async fail(error: Error, context?: any) {
    const duration = Date.now() - this.startTime;
    
    await logError(error, {
      backgroundTask: true,
      taskId: this.taskId,
      duration,
      checkpoints: this.checkpoints,
      ...context
    });
  }
}

// 8. Performance Monitor
export function trackPerformance(operationName: string) {
  const marks: Record<string, number> = {};
  
  return {
    mark: (label: string) => {
      marks[label] = performance.now();
    },
    
    measure: async (fromMark: string, toMark: string) => {
      if (!marks[fromMark] || !marks[toMark]) {
        console.warn(`Missing performance marks: ${fromMark} or ${toMark}`);
        return;
      }
      
      const duration = marks[toMark] - marks[fromMark];
      
      // Log slow operations as warnings
      if (duration > 1000) {
        errorTracker.addBreadcrumb({
          timestamp: new Date().toISOString(),
          type: 'custom',
          category: 'performance',
          message: `Slow operation detected: ${operationName}`,
          level: 'warning',
          data: {
            operation: operationName,
            fromMark,
            toMark,
            duration
          }
        });
        
        // Log as error if extremely slow (> 5s)
        if (duration > 5000) {
          await logError(new Error(`Performance issue: ${operationName} took ${duration}ms`), {
            performanceIssue: true,
            operation: operationName,
            duration,
            marks
          });
        }
      }
    }
  };
}